import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Briefcase,
  LineChart,
  ListOrdered,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Wallet,
  X,
} from "lucide-react";
import AppLiveClock from "../../ui/AppLiveClock";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

const BP_CHROME_CLOCK_BREAKPOINT = "(min-width: 1025px)";

const PortfolioChrome = ({
  activeTab,
  onTabChange,
  positionsCount,
  openOrdersCount,
  search,
  onSearchChange,
  refreshing,
  onRefresh,
  onMarkets,
  filtersOpen,
  onFiltersToggle,
  activeFilterCount,
}) => {
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const showClock = useMediaQuery(BP_CHROME_CLOCK_BREAKPOINT);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;
  const isHoldings = activeTab === "holdings";

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) onSearchChange("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (!String(search || "").trim() && !searchWrapRef.current?.contains(document.activeElement)) {
        setSearchOpen(false);
      }
    }, 120);
  };

  return (
    <header className="bp-chrome bp-chrome-markets bp-chrome-portfolio">
      <div className="bp-chrome-markets-bar">
        <nav
          className="bp-chrome-mode bp-chrome-markets-tabs"
          role="tablist"
          aria-label="BondPredict desk"
        >
          <button
            type="button"
            role="tab"
            aria-selected={false}
            className="bp-chrome-mode-btn"
            onClick={onMarkets}
          >
            <LineChart size={11} strokeWidth={2.4} aria-hidden />
            <span>Markets</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected
            className="bp-chrome-mode-btn bp-chrome-mode-btn-on"
          >
            <Briefcase size={11} strokeWidth={2.4} aria-hidden />
            <span>Portfolio</span>
          </button>
        </nav>

        {showClock ? (
          <div className="bp-chrome-center app-chrome-clock-center">
            <AppLiveClock className="app-chrome-clock" />
          </div>
        ) : null}

        <div className="bp-chrome-trail bp-chrome-markets-trail">
          <div className="bp-chrome-toolbar" role="toolbar" aria-label="Portfolio actions">
            <div
              ref={searchWrapRef}
              className={`bp-chrome-search ${isExpanded ? "bp-chrome-search-open" : ""} ${
                hasQuery ? "bp-chrome-search-active" : ""
              }`}
            >
              {!isExpanded ? (
                <button
                  type="button"
                  className="bp-chrome-search-toggle"
                  onClick={openSearch}
                  aria-label="Search portfolio"
                  aria-expanded={false}
                >
                  <Search size={15} strokeWidth={2.3} aria-hidden />
                </button>
              ) : (
                <>
                  <Search size={14} aria-hidden className="bp-chrome-search-icon" />
                  <input
                    ref={inputRef}
                    type="search"
                    className="bp-chrome-search-input"
                    placeholder={isHoldings ? "Search holdings…" : "Search orders…"}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onBlur={handleBlur}
                    aria-label="Search portfolio"
                  />
                  {hasQuery ? (
                    <button
                      type="button"
                      className="bp-chrome-search-clear"
                      onClick={() => onSearchChange("")}
                      aria-label="Clear search"
                    >
                      <X size={12} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="bp-chrome-search-clear"
                      onClick={closeSearch}
                      aria-label="Close search"
                    >
                      <X size={12} />
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="bp-chrome-actions">
              <button
                type="button"
                className={`bp-chrome-btn bp-chrome-btn-filters${
                  filtersOpen || activeFilterCount ? " bp-chrome-btn-filters-active" : ""
                }`}
                onClick={onFiltersToggle}
                aria-label="Portfolio filters"
                aria-expanded={filtersOpen}
                title="Filters"
              >
                <SlidersHorizontal size={15} strokeWidth={2.2} />
                {activeFilterCount ? (
                  <span className="bp-chrome-filter-badge">{activeFilterCount}</span>
                ) : null}
              </button>
              <button
                type="button"
                className="bp-chrome-btn bp-chrome-btn-refresh"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="Refresh portfolio"
                title="Refresh"
              >
                <RefreshCw size={15} className={refreshing ? "bp-spin" : ""} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bp-port-mobile-bar">
        <div
          className="bp-chrome-mode bp-chrome-portfolio-tabs"
          role="tablist"
          aria-label="Portfolio view"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isHoldings}
            className={`bp-chrome-mode-btn${isHoldings ? " bp-chrome-mode-btn-on" : ""}`}
            onClick={() => onTabChange?.("holdings")}
          >
            <Wallet size={11} strokeWidth={2.4} aria-hidden />
            <span>Holdings</span>
            <span className="bp-port-tab-count">{positionsCount ?? 0}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isHoldings}
            className={`bp-chrome-mode-btn${!isHoldings ? " bp-chrome-mode-btn-on" : ""}`}
            onClick={() => onTabChange?.("orders")}
          >
            <ListOrdered size={11} strokeWidth={2.4} aria-hidden />
            <span>Orders</span>
            <span className="bp-port-tab-count">{openOrdersCount ?? 0}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default PortfolioChrome;
